import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Dropdown from "../common/Dropdown";
import Button from "../common/Button";
import {
  playlistFilterSchema,
  PlaylistFilterFormData,
} from "../../schemas/playlistSchema";
import Modal from "../common/Modal";
import { usePlaylistTagOptions } from "../../hooks/usePlaylistTagOptions";

interface PlaylistFormProps {
  visible: boolean;
  initialData?: Partial<PlaylistFilterFormData>;
  onSubmit: (data: PlaylistFilterFormData) => void;
  onCancel: () => void;
  onClose: () => void;
  isLoading?: boolean;
  showTag?: boolean;
  // Optional callback to clear applied filters in parent while keeping the modal open
  onReset?: () => void;
}

const typeOptions = [
  { value: "main", label: "Main" },
  { value: "sub", label: "Sub" },
];

const PlaylistFilterForm: React.FC<PlaylistFormProps> = ({
  visible,
  initialData,
  onSubmit,
  onCancel,
  onClose,
  isLoading = false,
  showTag = true,
  onReset,
}) => {
  const {
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
    trigger,
  } = useForm<PlaylistFilterFormData>({
    resolver: zodResolver(playlistFilterSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: initialData,
  });

  const { playlistTagOptions, isPlaylistTagOptionsLoading } =
    usePlaylistTagOptions();

  // Reset form when initialData changes
  React.useEffect(() => {
    reset(initialData);
  }, [initialData, reset]);

  const handleReset = () => {
    // Clear form fields
    reset({});
    // Let parent clear its applied filters but keep modal open
    onReset?.();
  };

  return (
    <Modal isOpen={visible} onClose={onClose} title={"Filters"}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Dropdown
          label="Type"
          options={typeOptions}
          value={watch("type")}
          name="type"
          onChange={(val) => setValue("type", val as "main" | "sub", { shouldValidate: false })}
          onBlur={() => trigger("type")}
          error={errors.type}
        />
        {showTag && (
          <Dropdown
            label="Tag"
            options={playlistTagOptions || []}
            value={watch("tag")}
            name="tag"
            onChange={(val) => setValue("tag", val as string, { shouldValidate: false })}
            onBlur={() => trigger("tag")}
            error={errors.tag as any}
            disabled={isPlaylistTagOptionsLoading}
          />
        )}
        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            variant="secondary"
            onClick={handleReset}
            disabled={isLoading}
          >
            Reset
          </Button>
          <Button
            type="submit"
            variant="primary"
            isLoading={isLoading}
            className="w-full sm:w-auto"
          >
            Apply
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default PlaylistFilterForm;
